import { ReactNode, useMemo } from 'react'
import { Link } from 'react-router-dom'

import { FLAGS, useFeatureFlag } from '@/lib/hooks/useFeatureFlag'
import { useNavLayout } from '@/lib/hooks/useNavLayout'
import { useAuth } from '@/lib/provider/AuthProvider'
import UserAvatar from '@/components/UserAvatar'
import { cn } from '@/lib/misc/Utils'

import AdjustmentsIcon from '@icons/adjustments-vertical'
import LogoutIcon from '@icons/arrow-right-on-rectangle'
import ProfileIcon from '@icons/user-circle'
import SidebarIcon from '@icons/view-columns'
import TopBarIcon from '@icons/window'

export type AccountMenuTone = 'light' | 'dark'

interface AccountMenuProps {
  /** `dark` is for the translucent block inside the dark mobile drawer. */
  tone?: AccountMenuTone
  /** Render the avatar/name/email header. Omit when the trigger already shows it. */
  showIdentity?: boolean
  /** Fired after any item is activated, so callers can close their surface. */
  onNavigate?: () => void
  className?: string
}

const TONES: Record<
  AccountMenuTone,
  {
    surface: string
    item: string
    signOut: string
    icon: string
    label: string
    value: string
    block: string
    divider: string
  }
> = {
  light: {
    surface:
      'overflow-hidden rounded-xl border border-hairline bg-surface shadow-popover',
    item: 'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-neutral-60 transition-colors hover:bg-neutral-10',
    signOut:
      'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-status-error-700 transition-colors hover:bg-status-error-50',
    icon: 'flex size-4 shrink-0 items-center justify-center text-neutral-50',
    label: 'text-xs font-medium text-neutral-50',
    value: 'text-neutral-70',
    block: 'rounded-lg bg-neutral-10 p-3',
    divider: 'border-t border-hairline',
  },
  dark: {
    surface: 'rounded-lg bg-surface/10 p-2',
    item: 'flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left font-medium text-white/90 transition-colors hover:bg-surface/20',
    signOut:
      'flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left font-medium text-white/90 transition-colors hover:bg-surface/20',
    icon: 'flex size-5 shrink-0 items-center justify-center text-white/70',
    label: 'text-xs font-medium text-white/70',
    value: 'font-medium text-white',
    block: 'rounded-lg bg-surface/20 p-3',
    divider: 'border-t border-white/20',
  },
}

/**
 * The single account menu, shared by the toolbar popover, the toolbar's mobile
 * drawer and the sidebar. Previously this existed as three hand-written copies,
 * so an item added in one place silently went missing from the others.
 */
const AccountMenu = ({
  tone = 'light',
  showIdentity = true,
  onNavigate,
  className,
}: AccountMenuProps) => {
  const [auth] = useAuth()
  const { isSidebar, toggleLayout } = useNavLayout()
  const isUserAnalyticsEnabled = useFeatureFlag(FLAGS.USER_ANALYTICS)
  const t = TONES[tone]
  const user = auth.user

  const { userInitials, userFullName } = useMemo(() => {
    const email = typeof user.email === 'string' ? user.email : ''
    const emailLocal = email ? email.split('@')[0] : ''
    return {
      userFullName:
        user.first_name && user.last_name
          ? `${user.first_name} ${user.last_name}`
          : emailLocal || email,
      userInitials:
        user.first_name && user.last_name
          ? `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`
          : (email || user.first_name || user.last_name || '')
              .charAt(0)
              .toUpperCase(),
    }
  }, [user.email, user.first_name, user.last_name])

  const row = (icon: ReactNode, label: string) => (
    <>
      <span className={t.icon}>{icon}</span>
      <span className="min-w-0 truncate">{label}</span>
    </>
  )

  return (
    <div className={cn(t.surface, className)}>
      {showIdentity && (
        <div className="px-3 pt-3 pb-2">
          <div className="flex items-center gap-3">
            <UserAvatar
              initials={userInitials}
              size="lg"
              tone={tone === 'dark' ? 'inverted' : 'neutral'}
            />
            <div className="min-w-0 flex-1">
              <p className={cn('truncate font-semibold', t.value)}>
                {userFullName}
              </p>
              <p className={cn('truncate text-sm', t.label)}>{user.email}</p>
            </div>
          </div>
        </div>
      )}

      {/* Without the identity header above, the company panel is the menu's
          first child and needs the same inset from the top edge that it already
          has on its sides. */}
      {user.company?.name && (
        <div className={cn('mx-2 mb-2', showIdentity ? 'mt-1' : 'mt-2')}>
          <div className={t.block}>
            <p className={cn('mb-1', t.label)}>Company</p>
            <p className={cn('truncate', t.value)}>{user.company.name}</p>
            {user.company.cid && (
              <p className={cn('mt-1', t.label)}>{user.company.cid}</p>
            )}
          </div>
        </div>
      )}

      <div className={t.divider} role="separator" aria-hidden />

      <div className="flex flex-col gap-0.5 p-2">
        <Link
          data-cy="menu-profile"
          to="/user-profile"
          onClick={onNavigate}
          className={t.item}
        >
          {row(<ProfileIcon className="size-full" />, 'My Profile')}
        </Link>

        {isUserAnalyticsEnabled && (
          <Link
            to="/settings/analytics"
            onClick={onNavigate}
            className={t.item}
          >
            {row(<AdjustmentsIcon className="size-full" />, 'Analytics')}
          </Link>
        )}

        <button
          type="button"
          data-cy="menu-nav-layout"
          onClick={() => {
            onNavigate?.()
            toggleLayout()
          }}
          className={cn(t.item, 'cursor-pointer')}
        >
          {row(
            isSidebar ? (
              <TopBarIcon className="size-full" />
            ) : (
              <SidebarIcon className="size-full" />
            ),
            isSidebar ? 'Use top bar' : 'Use sidebar'
          )}
        </button>
      </div>

      <div className={t.divider} role="separator" aria-hidden />

      <div className="p-2">
        <Link to="/logout" onClick={onNavigate} className={t.signOut}>
          {row(<LogoutIcon className="size-full" />, 'Sign Out')}
        </Link>
      </div>
    </div>
  )
}

export default AccountMenu
